import MindrailLogo from "../components/landing/MindrailLogo";

const policySections = [
  {
    title: "1. What Mindrail is",
    body: [
      "Mindrail is a privacy-first Chrome extension that helps you recover focus, reduce browser clutter, park distracting thoughts, and continue your current browsing task.",
      "Mindrail is a focus and recovery tool. It is not a medical or health product and does not diagnose, treat, monitor, or medically manage ADHD or any other health condition.",
    ],
  },
  {
    title: "2. Local-first extension data",
    body: [
      "Mindrail is designed to work local-first by default. Core extension data such as tasks, parked thoughts, recovery history, focus state, settings, timers, Reading Rail state, and Clean Mode preferences are stored in Chrome local extension storage on your device.",
      "This local extension data is not sold, not used for advertising, and not used to create shame-based productivity scoring.",
    ],
  },
  {
    title: "3. Optional account and billing data",
    body: [
      "You can use Mindrail's core local-first experience without creating an account. Optional Pro features may require authentication and billing.",
      "When you sign in or subscribe, authentication may be handled by Supabase and billing may be handled by Stripe. Mindrail does not store full card numbers. Stripe processes payment details under its own payment and security systems.",
    ],
  },
  {
    title: "4. Optional AI data",
    body: [
      "Mindrail can be used without AI. Optional AI features are available only when you enable the relevant AI mode or request an AI-powered feature.",
      "Depending on your settings, Mindrail may support local logic, a user-provided AI key, or Mindrail Cloud AI. When an AI mode is enabled, Mindrail sends only the context needed to create the requested suggestion. This may include recovery text, Brain Dump text, selected task context, recent Mindrail activity summaries, page summaries, page metadata, current-page context, or a current-tab visual recovery screenshot when you explicitly request visual recovery.",
      "AI controls are separate from telemetry controls. You can control page summaries, memory summaries, Cloud AI, visual recovery, and telemetry in Mindrail Settings.",
    ],
  },
  {
    title: "5. Telemetry",
    body: [
      "Mindrail may use privacy-safe telemetry to understand extension reliability, errors, and feature usage. Telemetry is not used for advertising and is separate from optional AI features.",
      "Mindrail may use Sentry for error diagnostics and PostHog for anonymous product analytics when production telemetry keys are configured and the user has not opted out.",
      "Telemetry controls are available in Settings. Mindrail does not use telemetry to collect page text, URLs, task text, parked thought content, AI prompts, full browsing history, or medical information.",
    ],
  },
  {
    title: "6. Website and page context",
    body: [
      "Mindrail may read current-page context when you use browser-context features such as Clean Mode, Reading Rail, page-aware recovery, or visual recovery. Page context is used to provide the feature you requested.",
      "Private page content is not sent to an AI provider unless you enable the relevant AI mode and use a feature that requires that context. Mindrail is designed to keep local-first behavior available when AI is disabled.",
    ],
  },
  {
    title: "7. What Mindrail does not do",
    body: [
      "Mindrail does not sell your data.",
      "Mindrail does not use your data for advertising.",
      "Mindrail does not claim to provide medical diagnosis, treatment, monitoring, or emergency support.",
      "Mindrail does not inject remote scripts into extension pages.",
    ],
  },
  {
    title: "8. Data sharing",
    body: [
      "Mindrail may share limited data with service providers only when needed to provide requested features, such as authentication, billing, optional AI, hosting, security, and reliability services.",
      "Mindrail may also disclose information if required by law or to protect users, the product, or the public from fraud, abuse, or security threats.",
    ],
  },
  {
    title: "9. Your choices",
    body: [
      "You can use local-first features without enabling AI.",
      "You can change AI, telemetry, page-summary, memory-summary, and visual recovery controls in Settings.",
      "You can clear local extension data through Chrome extension storage controls or by removing the extension.",
      "If you use optional account or billing features, you can sign out, manage billing through the customer portal when available, or contact support.",
    ],
  },
  {
    title: "10. Contact",
    body: [
      "For privacy questions, support requests, or data questions, contact support@mindrail.co.",
      "This policy may be updated as Mindrail changes. Material changes will be reflected on this page.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <main className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      <header className="border-b border-[#E2E8F0] bg-white/90">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-6">
          <a href="/" aria-label="Mindrail home">
            <MindrailLogo size={58} />
          </a>
          <a
            href="/"
            className="rounded-full border border-[#CBD5E1] px-4 py-2 text-sm font-medium text-[#475569] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]"
          >
            Back to site
          </a>
        </div>
      </header>

      <article className="mx-auto max-w-4xl px-6 py-14 sm:py-20">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#2563EB]">
          Privacy Policy
        </p>
        <h1 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
          Mindrail Privacy Policy
        </h1>
        <p className="mt-5 text-base leading-7 text-[#475569]">
          Effective date: June 2, 2026
        </p>
        <p className="mt-6 text-lg leading-8 text-[#334155]">
          This policy explains how Mindrail handles information for its Chrome
          extension, website, optional AI features, authentication, billing, and
          support flows.
        </p>

        <div className="mt-12 space-y-10">
          {policySections.map((section) => (
            <section key={section.title}>
              <h2 className="font-display text-2xl font-medium tracking-tight">
                {section.title}
              </h2>
              <div className="mt-4 space-y-4">
                {section.body.map((paragraph) => (
                  <p key={paragraph} className="text-base leading-7 text-[#475569]">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </article>
    </main>
  );
}
